import React, { useEffect, useRef, useState } from "react";

const DivyaPortfolio = () => {
  const [visible, setVisible] = useState({});
  const refs = useRef({});

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach(e => {
        if (e.isIntersecting) setVisible(p => ({ ...p, [e.target.id]: true }));
      }),
      { threshold: 0.1 }
    );
    Object.values(refs.current).forEach(el => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const reg = id => el => { refs.current[id] = el; };
  const fade = (id, delay = 0) => ({
    opacity: visible[id] ? 1 : 0,
    transform: visible[id] ? "translateY(0)" : "translateY(32px)",
    transition: `opacity 0.75s ease ${delay}s, transform 0.75s ease ${delay}s`,
  });
  const fadeL = (id, delay = 0) => ({
    opacity: visible[id] ? 1 : 0,
    transform: visible[id] ? "translateX(0)" : "translateX(-28px)",
    transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`,
  });

  const css = `
    @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,700;0,9..144,900;1,9..144,700&family=Instrument+Sans:wght@400;500;600;700&display=swap');

    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    :root {
      --bg:          #080C10;
      --bg2:         #0B1018;
      --surface:     #0F1520;
      --card:        #131C28;
      --card2:       #182130;
      --border:      #1E2D3D;
      --border2:     #243548;

      --cyan:        #06B6D4;
      --cyan-hot:    #22D3EE;
      --cyan-bright: #67E8F9;
      --cyan-dim:    rgba(6,182,212,0.1);
      --cyan-glow:   rgba(6,182,212,0.2);
      --cyan-border: rgba(6,182,212,0.3);

      --blue:        #3B82F6;
      --blue-dim:    rgba(59,130,246,0.08);

      --white:       #F0F9FF;
      --silver:      #BAD4E8;
      --muted:       #6B8CAE;
      --faint:       #3A5570;
    }

    html { scroll-behavior: smooth; }

    .dj {
      font-family: 'Instrument Sans', sans-serif;
      background: var(--bg);
      color: var(--white);
      min-height: 100vh;
      overflow-x: hidden;
    }

    /* ── GRAIN ── */
    .dj-grain {
      position: fixed; inset: 0; z-index: 0; pointer-events: none; opacity: 0.025;
      background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
      background-size: 256px 256px;
    }

    /* ── AMBIENT GLOWS ── */
    .dj-glow1 {
      position: fixed; top: -250px; right: -150px;
      width: 700px; height: 700px; border-radius: 50%;
      background: radial-gradient(circle, rgba(6,182,212,0.07) 0%, transparent 65%);
      pointer-events: none; z-index: 0;
    }
    .dj-glow2 {
      position: fixed; bottom: -200px; left: -100px;
      width: 600px; height: 600px; border-radius: 50%;
      background: radial-gradient(circle, rgba(59,130,246,0.06) 0%, transparent 65%);
      pointer-events: none; z-index: 0;
    }

    /* ── SCANLINES ── */
    .dj-scanlines {
      position: fixed; inset: 0; z-index: 0; pointer-events: none; opacity: 0.015;
      background: repeating-linear-gradient(
        0deg, transparent, transparent 2px,
        rgba(6,182,212,1) 2px, rgba(6,182,212,1) 3px
      );
    }

    /* ── NAV ── */
    .dj-nav {
      position: fixed; top: 0; left: 0; right: 0; z-index: 100;
      height: 68px; padding: 0 6vw;
      display: flex; align-items: center; justify-content: space-between;
      background: rgba(8,12,16,0.85);
      backdrop-filter: blur(24px);
      border-bottom: 1px solid var(--border);
    }
    .dj-nav-left { display: flex; align-items: center; gap: 12px; }
    .dj-nav-pulse {
      width: 8px; height: 8px; border-radius: 50%;
      background: var(--cyan);
      box-shadow: 0 0 10px var(--cyan);
      animation: dj-blink 2s ease infinite;
    }
    @keyframes dj-blink { 0%,100%{opacity:1;box-shadow:0 0 10px var(--cyan)} 50%{opacity:0.3;box-shadow:none} }
    .dj-nav-tag {
      font-size: 11px; font-weight: 600; letter-spacing: 3px;
      text-transform: uppercase; color: var(--muted);
    }
    .dj-nav-links { display: flex; gap: 32px; }
    .dj-nav-a {
      font-size: 12px; font-weight: 500; letter-spacing: 1px;
      text-transform: uppercase; color: var(--muted);
      text-decoration: none; transition: color 0.2s;
    }
    .dj-nav-a:hover { color: var(--cyan); }
    @media(max-width:768px){ .dj-nav-links { display: none; } }

    /* ── HERO ── */
    .dj-hero {
      position: relative; z-index: 1;
      min-height: 100vh;
      display: grid; grid-template-columns: 1fr 440px;
      padding-top: 68px; overflow: hidden;
    }
    @media(max-width:960px){ .dj-hero { grid-template-columns: 1fr; } .dj-hero-right { display: none; } }

    .dj-hero-left {
      display: flex; flex-direction: column; justify-content: center;
      padding: 80px 60px 80px 6vw;
    }

    @keyframes dj-up { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }

    /* ── PROFILE IMAGE ── */
    .dj-profile-wrap {
      width: 96px; height: 96px;
      border-radius: 50%; overflow: hidden; flex-shrink: 0;
      border: 2px solid var(--cyan);
      box-shadow: 0 0 0 5px rgba(6,182,212,0.12), 0 0 24px rgba(6,182,212,0.2);
      margin-bottom: 28px;
      opacity: 0; animation: dj-up 0.7s ease 0.2s forwards;
      background: var(--card);
    }
    .dj-profile-wrap img {
      width: 100%; height: 100%;
      object-fit: cover; object-position: center top;
      display: block;
    }
    /* fallback initials shown when image fails */
    .dj-profile-fallback {
      width: 100%; height: 100%;
      display: flex; align-items: center; justify-content: center;
      font-family: 'Fraunces', serif; font-weight: 900; font-size: 32px;
      color: var(--cyan); background: var(--card);
    }

    .dj-hero-pre {
      font-family: 'Fraunces', serif; font-style: italic; font-weight: 300;
      font-size: clamp(17px,2vw,22px); color: var(--muted);
      margin-bottom: 10px; letter-spacing: 0.5px;
      opacity: 0; animation: dj-up 0.7s ease 0.3s forwards;
    }

    .dj-name {
      font-family: 'Fraunces', serif; font-weight: 900;
      font-size: clamp(50px, 6.5vw, 92px);
      line-height: 0.92; letter-spacing: -3px; color: var(--white);
      margin-bottom: 28px;
      opacity: 0; animation: dj-up 0.8s ease 0.5s forwards;
    }
    .dj-name-line2 {
      display: block;
      background: linear-gradient(135deg, var(--cyan), var(--cyan-hot), var(--cyan-bright));
      -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
      position: relative;
      filter: drop-shadow(0 0 24px rgba(6,182,212,0.4));
    }
    .dj-name-line2::after {
      content: '';
      position: absolute; left: 0; bottom: 4px;
      width: 50%; height: 3px;
      background: linear-gradient(90deg, var(--cyan), transparent);
      border-radius: 2px;
    }

    .dj-hero-desc {
      font-size: clamp(15px,1.6vw,17px); font-weight: 400; line-height: 1.85;
      color: var(--muted); max-width: 440px; margin-bottom: 48px;
      padding-left: 20px; border-left: 2px solid var(--cyan);
      opacity: 0; animation: dj-up 0.7s ease 0.7s forwards;
    }
    .dj-hero-desc strong { color: var(--silver); font-weight: 600; }

    .dj-hero-stats {
      display: flex; width: fit-content;
      border: 1px solid var(--border2); border-radius: 10px; overflow: hidden;
      background: var(--surface);
      opacity: 0; animation: dj-up 0.7s ease 0.9s forwards;
      box-shadow: 0 8px 40px rgba(0,0,0,0.4), 0 0 0 1px rgba(6,182,212,0.05);
    }
    .dj-stat {
      padding: 22px 32px; text-align: center;
      border-right: 1px solid var(--border); position: relative;
    }
    .dj-stat:last-child { border-right: none; }
    .dj-stat::after {
      content: ''; position: absolute; bottom: 0; left: 25%; right: 25%; height: 2px;
      background: linear-gradient(90deg, transparent, var(--cyan), transparent);
    }
    .dj-stat-n {
      font-family: 'Fraunces', serif; font-size: 36px; font-weight: 900;
      background: linear-gradient(135deg, var(--cyan), var(--cyan-bright));
      -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
      line-height: 1;
    }
    .dj-stat-l { font-size: 10px; font-weight: 600; letter-spacing: 2px; text-transform: uppercase; color: var(--faint); margin-top: 5px; }

    /* ── HERO RIGHT ── */
    .dj-hero-right {
      position: relative; overflow: hidden;
      background: var(--bg2); border-left: 1px solid var(--border);
    }
    .dj-hero-right::before {
      content: ''; position: absolute; inset: 0;
      background:
        radial-gradient(ellipse 80% 50% at 50% 20%, rgba(6,182,212,0.08), transparent 60%),
        radial-gradient(ellipse 60% 80% at 20% 90%, rgba(59,130,246,0.06), transparent 60%);
    }
    .dj-big-m {
      position: absolute; top: 50%; left: 50%;
      transform: translate(-50%, -50%);
      font-family: 'Fraunces', serif; font-weight: 900; font-style: italic;
      font-size: 300px; line-height: 1; letter-spacing: -15px;
      color: rgba(6,182,212,0.04); user-select: none; pointer-events: none;
    }
    .dj-float-col {
      position: relative; z-index: 2;
      height: 100%; display: flex; flex-direction: column;
      justify-content: center; gap: 14px; padding: 48px 36px;
    }
    .dj-fcard {
      background: var(--card); border: 1px solid var(--border2); border-radius: 12px;
      padding: 18px 22px; display: flex; align-items: center; gap: 14px;
      box-shadow: 0 4px 24px rgba(0,0,0,0.4);
      animation: dj-float 4s ease-in-out infinite;
      transition: border-color 0.3s, box-shadow 0.3s;
    }
    .dj-fcard:hover { border-color: var(--cyan-border); box-shadow: 0 4px 32px rgba(6,182,212,0.15); }
    .dj-fcard:nth-child(1) { animation-delay: 0s;    margin-left: 0; }
    .dj-fcard:nth-child(2) { animation-delay: 0.7s;  margin-left: 28px; }
    .dj-fcard:nth-child(3) { animation-delay: 1.4s;  margin-left: 12px; }
    .dj-fcard:nth-child(4) { animation-delay: 2.1s;  margin-left: 40px; }
    @keyframes dj-float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }
    .dj-fcard-ico { font-size: 24px; flex-shrink: 0; }
    .dj-fcard-label { font-size: 14px; font-weight: 600; color: var(--white); }
    .dj-fcard-sub { font-size: 11px; color: var(--muted); margin-top: 2px; }
    .dj-fcard-badge {
      margin-left: auto; padding: 3px 12px; border-radius: 50px; flex-shrink: 0;
      font-size: 10px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase;
      background: var(--cyan-dim); color: var(--cyan); border: 1px solid var(--cyan-border);
    }
    .dj-side-label {
      position: absolute; right: 18px; top: 50%;
      transform: translateY(-50%) rotate(90deg);
      font-family: 'Fraunces', serif; font-style: italic; font-size: 11px;
      color: var(--faint); letter-spacing: 4px; white-space: nowrap;
    }

    /* ── TICKER ── */
    .dj-ticker {
      position: relative; z-index: 1;
      border-top: 1px solid var(--border); border-bottom: 1px solid var(--border);
      background: var(--surface); padding: 16px 0; overflow: hidden;
    }
    .dj-ticker-track {
      display: flex; width: max-content;
      animation: dj-run 24s linear infinite;
    }
    @keyframes dj-run { from{transform:translateX(0)} to{transform:translateX(-50%)} }
    .dj-ticker-item {
      display: flex; align-items: center; gap: 18px; padding: 0 28px;
      font-size: 11px; font-weight: 600; letter-spacing: 2.5px; text-transform: uppercase;
      color: var(--faint); white-space: nowrap;
    }
    .dj-ticker-dot { width: 4px; height: 4px; border-radius: 50%; background: var(--cyan); flex-shrink: 0; }

    /* ── SECTION ── */
    .dj-sec { position: relative; z-index: 1; padding: 110px 6vw; }
    .dj-sec + .dj-sec { border-top: 1px solid var(--border); }

    .dj-eyebrow {
      display: flex; align-items: center; gap: 12px;
      font-size: 10px; font-weight: 700; letter-spacing: 4px;
      text-transform: uppercase; color: var(--cyan); margin-bottom: 14px;
    }
    .dj-eyebrow::before { content: ''; display: block; width: 22px; height: 2px; background: var(--cyan); }

    .dj-sec-title {
      font-family: 'Fraunces', serif; font-weight: 900;
      font-size: clamp(34px, 5vw, 58px); line-height: 1.0;
      letter-spacing: -2px; color: var(--white); margin-bottom: 60px;
    }
    .dj-sec-title em { font-style: italic; color: var(--cyan); font-weight: 700; }

    /* ── ABOUT ── */
    .dj-about-grid { display: grid; grid-template-columns: 5fr 4fr; gap: 80px; align-items: start; }
    @media(max-width:900px){ .dj-about-grid { grid-template-columns: 1fr; gap: 48px; } }

    .dj-about-text { font-size: 17px; line-height: 2.0; color: var(--muted); font-weight: 400; }
    .dj-about-text strong { color: var(--silver); font-weight: 600; }
    .dj-about-text p + p { margin-top: 22px; }

    .dj-facts { display: flex; flex-direction: column; }
    .dj-fact {
      display: flex; justify-content: space-between; align-items: center;
      padding: 16px 8px; border-bottom: 1px solid var(--border);
      transition: padding 0.25s, background 0.2s; border-radius: 4px;
    }
    .dj-fact:first-child { border-top: 1px solid var(--border); }
    .dj-fact:hover { padding-left: 16px; background: rgba(6,182,212,0.04); }
    .dj-fk { font-size: 10px; letter-spacing: 2px; text-transform: uppercase; color: var(--faint); font-weight: 600; }
    .dj-fv { font-size: 13px; font-weight: 600; color: var(--silver); }
    .dj-fv.cy { color: var(--cyan); }

    /* ── STACK CARDS ── */
    .dj-stack-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 16px; }
    .dj-sc {
      padding: 34px 28px; background: var(--card);
      border: 1px solid var(--border); border-radius: 12px;
      position: relative; overflow: hidden; cursor: default;
      transition: transform 0.3s, border-color 0.3s, box-shadow 0.3s;
    }
    .dj-sc:hover {
      transform: translateY(-6px);
      border-color: var(--cyan-border);
      box-shadow: 0 20px 60px rgba(6,182,212,0.1);
    }
    .dj-sc::before {
      content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px;
      background: linear-gradient(90deg, var(--cyan), var(--blue));
      transform: scaleX(0); transform-origin: left; transition: transform 0.4s;
    }
    .dj-sc:hover::before { transform: scaleX(1); }
    .dj-sc-ghost {
      position: absolute; top: 16px; right: 20px;
      font-family: 'Fraunces', serif; font-size: 52px; font-weight: 900;
      color: rgba(6,182,212,0.06); line-height: 1;
    }
    .dj-sc-ico { font-size: 30px; margin-bottom: 18px; }
    .dj-sc-title { font-size: 15px; font-weight: 700; color: var(--white); margin-bottom: 10px; }
    .dj-sc-desc { font-size: 13px; color: var(--muted); line-height: 1.8; }

    /* ── PROJECTS ── */
    .dj-proj-list { display: flex; flex-direction: column; }
    .dj-proj {
      display: grid; grid-template-columns: 72px 1fr 44px;
      gap: 28px; align-items: center;
      padding: 26px 28px;
      background: var(--card); border: 1px solid var(--border);
      transition: background 0.25s, padding-left 0.3s, border-color 0.25s; cursor: default;
    }
    .dj-proj:first-child { border-radius: 12px 12px 0 0; }
    .dj-proj:last-child  { border-radius: 0 0 12px 12px; }
    .dj-proj + .dj-proj  { border-top: none; }
    .dj-proj:hover { background: var(--card2); padding-left: 40px; border-color: var(--cyan-border); }
    .dj-proj-n {
      font-family: 'Fraunces', serif; font-size: 34px; font-weight: 900;
      color: var(--border2); text-align: center; line-height: 1;
      transition: color 0.25s;
    }
    .dj-proj:hover .dj-proj-n { color: var(--cyan); }
    .dj-proj-title { font-size: 16px; font-weight: 700; color: var(--white); margin-bottom: 5px; }
    .dj-proj-desc { font-size: 13px; color: var(--muted); line-height: 1.6; }
    .dj-proj-tags { display: flex; gap: 7px; margin-top: 10px; flex-wrap: wrap; }
    .dj-ptag {
      padding: 3px 10px; border-radius: 50px; font-size: 10px; font-weight: 600;
      background: var(--surface); color: var(--muted); border: 1px solid var(--border2);
    }
    .dj-ptag.cy { background: var(--cyan-dim); color: var(--cyan); border-color: var(--cyan-border); }
    .dj-proj-arr {
      width: 38px; height: 38px; border-radius: 50%;
      border: 1px solid var(--border2); display: flex; align-items: center;
      justify-content: center; color: var(--muted); font-size: 15px; flex-shrink: 0;
      transition: background 0.2s, color 0.2s, border-color 0.2s, box-shadow 0.2s;
    }
    .dj-proj:hover .dj-proj-arr {
      background: var(--cyan); border-color: var(--cyan); color: var(--bg);
      box-shadow: 0 0 16px var(--cyan-glow);
    }

    /* ── CERTIFICATIONS ── */
    .dj-cert-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 20px; }
    @media(max-width:768px){ .dj-cert-grid { grid-template-columns: 1fr; } }
    .dj-cert {
      padding: 40px 32px; background: var(--card); border: 1px solid var(--border); border-radius: 14px;
      position: relative; overflow: hidden; cursor: default;
      transition: transform 0.3s, box-shadow 0.3s, border-color 0.3s;
    }
    .dj-cert:hover {
      transform: translateY(-6px);
      border-color: var(--cyan-border);
      box-shadow: 0 20px 60px rgba(6,182,212,0.12);
    }
    .dj-cert::after {
      content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 3px;
      background: linear-gradient(90deg, var(--cyan), var(--blue));
      transform: scaleX(0); transform-origin: left; transition: transform 0.4s;
    }
    .dj-cert:hover::after { transform: scaleX(1); }
    .dj-cert::before {
      content: ''; position: absolute; top: -40px; right: -40px;
      width: 130px; height: 130px; border-radius: 50%;
      background: radial-gradient(circle, rgba(6,182,212,0.07), transparent 70%);
    }
    .dj-cert-orb {
      width: 56px; height: 56px; border-radius: 12px; margin-bottom: 24px;
      background: var(--cyan-dim); border: 1px solid var(--cyan-border);
      display: flex; align-items: center; justify-content: center; font-size: 26px;
      position: relative; z-index: 1;
    }
    .dj-cert-name { font-size: 15px; font-weight: 700; color: var(--white); margin-bottom: 6px; line-height: 1.3; position: relative; z-index: 1; }
    .dj-cert-org { font-size: 11px; color: var(--faint); letter-spacing: 1.5px; text-transform: uppercase; margin-bottom: 20px; position: relative; z-index: 1; }
    .dj-cert-badge {
      display: inline-block; padding: 4px 14px; border-radius: 50px;
      font-size: 10px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase;
      background: var(--cyan-dim); color: var(--cyan); border: 1px solid var(--cyan-border);
      position: relative; z-index: 1;
    }

    /* ── TECH PILLS ── */
    .dj-tech-grid {
      display: grid; grid-template-columns: repeat(2,1fr); gap: 2px;
      border: 1px solid var(--border); border-radius: 12px; overflow: hidden;
      background: var(--border);
    }
    @media(max-width:700px){ .dj-tech-grid { grid-template-columns: 1fr; } }
    .dj-tg { padding: 36px 32px; background: var(--card); }
    .dj-tg-title {
      font-size: 10px; font-weight: 700; letter-spacing: 3px; text-transform: uppercase;
      color: var(--cyan); margin-bottom: 20px; padding-bottom: 12px;
      border-bottom: 1px solid var(--border2);
    }
    .dj-pills { display: flex; flex-wrap: wrap; gap: 8px; }
    .dj-pill {
      padding: 7px 15px; font-size: 12px; font-weight: 500;
      color: var(--muted); background: var(--surface);
      border: 1px solid var(--border2); border-radius: 4px;
      transition: all 0.2s; cursor: default;
    }
    .dj-pill:hover { color: var(--white); border-color: var(--cyan-border); background: var(--cyan-dim); }
    .dj-pill.hot { color: var(--cyan); border-color: var(--cyan-border); background: var(--cyan-dim); font-weight: 600; }

    /* ── FOOTER ── */
    .dj-footer {
      position: relative; z-index: 1; border-top: 1px solid var(--border);
      padding: 60px 6vw 40px; background: var(--bg2);
    }
    .dj-footer-top { display: grid; grid-template-columns: 1fr 1fr; gap: 60px; margin-bottom: 48px; }
    @media(max-width:768px){ .dj-footer-top { grid-template-columns: 1fr; gap: 32px; } }
    .dj-footer-role {
      font-family: 'Fraunces', serif; font-weight: 900; font-size: 36px;
      color: var(--white); line-height: 1.1; margin-bottom: 12px;
    }
    .dj-footer-role span {
      font-style: italic;
      background: linear-gradient(135deg, var(--cyan), var(--cyan-bright));
      -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
    }
    .dj-footer-sub { font-size: 14px; color: var(--muted); line-height: 1.75; max-width: 320px; }
    .dj-footer-certs { display: flex; flex-direction: column; gap: 10px; }
    .dj-footer-cert {
      display: flex; align-items: center; gap: 12px; padding: 14px 18px;
      background: var(--surface); border: 1px solid var(--border); border-radius: 8px;
      font-size: 13px; color: var(--muted); font-weight: 500;
      transition: border-color 0.2s, background 0.2s;
    }
    .dj-footer-cert:hover { border-color: var(--cyan-border); background: var(--cyan-dim); }
    .dj-footer-cert-ico { font-size: 18px; }
    .dj-footer-bottom {
      display: flex; justify-content: space-between; align-items: center;
      padding-top: 28px; border-top: 1px solid var(--border); flex-wrap: wrap; gap: 12px;
    }
    .dj-footer-copy { font-size: 12px; color: var(--faint); }
    .dj-footer-loc  { font-size: 12px; color: var(--faint); }

    /* ── SCROLLBAR ── */
    ::-webkit-scrollbar { width: 3px; }
    ::-webkit-scrollbar-track { background: var(--bg); }
    ::-webkit-scrollbar-thumb { background: var(--cyan); border-radius: 2px; }
  `;

  const ticker = ["React.js","Node.js","Express.js","MongoDB","Docker","AWS","GitHub","REST APIs","MERN Stack","Real-Time Apps","Full Stack","CI/CD"];

  const stacks = [
    { ico:"⚛️", n:"01", title:"Frontend · React.js", desc:"Component-driven UIs with React hooks, state management, and real-time interactive experiences that delight users." },
    { ico:"🟢", n:"02", title:"Backend · Node.js", desc:"High-performance REST APIs with Express.js, middleware design, JWT authentication, and scalable server architecture." },
    { ico:"🍃", n:"03", title:"Database · MongoDB", desc:"MongoDB Associate certified — schema design, aggregation pipelines, indexing, and production-grade Mongoose integration." },
    { ico:"🐳", n:"04", title:"DevOps · Docker & AWS", desc:"Docker containerization, AWS (EC2, S3) deployments, GitHub Actions CI/CD, and automated production pipelines." },
    { ico:"⚡", n:"05", title:"Real-Time Projects", desc:"WebSocket-driven live apps, event-driven architecture, and end-to-end full-stack ownership from idea to deployment." },
  ];

  const projects = [
    { title:"E-Commerce Platform", desc:"Full-stack MERN app with real-time inventory, payment gateway, and admin dashboard. Containerized with Docker, deployed on AWS EC2.", tags:[["React","cy"],["Node.js",""],["MongoDB",""],["Docker",""]] },
    { title:"Real-Time Chat App", desc:"WebSocket-powered messaging with JWT auth, message history, live typing indicators, and user presence tracking.", tags:[["Socket.io","cy"],["Express.js",""],["JWT",""],["React",""]] },
    { title:"Task Management System", desc:"Collaborative project tracker with role-based access, drag-and-drop boards, and real-time sync via REST + WebSockets.", tags:[["MERN","cy"],["AWS S3",""],["GitHub Actions",""],["CI/CD",""]] },
    { title:"Student Analytics Dashboard", desc:"Data visualization platform for academic performance. MongoDB aggregation pipeline powers complex multi-dimensional reporting.", tags:[["React","cy"],["MongoDB",""],["Node.js",""],["AWS",""]] },
  ];

  return (
    <>
      <style>{css}</style>
      <div className="dj">
        <div className="dj-grain" />
        <div className="dj-glow1" />
        <div className="dj-glow2" />
        <div className="dj-scanlines" />

        {/* NAV */}
        <nav className="dj-nav">
          <div className="dj-nav-left">
            <div className="dj-nav-pulse" />
            <span className="dj-nav-tag">MERN Developer</span>
          </div>
          <div className="dj-nav-links">
            {["About","Stack","Projects","Certifications"].map(l => (
              <a key={l} className="dj-nav-a" href={`#${l.toLowerCase()}`}>{l}</a>
            ))}
          </div>
        </nav>

        {/* HERO */}
        <section className="dj-hero">
          <div className="dj-hero-left">

            {/* ── PROFILE IMAGE — fixed: proper fixed size, top of column, with fallback ── */}
            <div className="dj-profile-wrap">
              <img
                src="https://adityauniversity.in/adtppu/SDG/5532.png"
                alt="Divya Jaswanthi"
                onError={e => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "flex";
                }}
              />
              <div className="dj-profile-fallback" style={{ display: "none" }}>DJ</div>
            </div>

            <p className="dj-hero-pre">Hello, I'm</p>

            <h1 className="dj-name">
              Divya<span className="dj-name-line2">Jaswanthi</span>
            </h1>

            <p className="dj-hero-desc">
              <strong>MERN Stack Developer</strong> building real-time, production-grade
              full-stack apps — React, Node.js, MongoDB, Docker &amp; AWS.
            </p>

            <div className="dj-hero-stats">
              {[["4+","Projects"],["3","Certs"],["6+","Tech Stack"]].map(([n,l]) => (
                <div className="dj-stat" key={l}>
                  <div className="dj-stat-n">{n}</div>
                  <div className="dj-stat-l">{l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* HERO RIGHT — floating tech cards */}
          <div className="dj-hero-right">
            <div className="dj-big-m">M</div>
            <div className="dj-float-col">
              {[
                { ico:"⚛️", label:"React.js",  sub:"Frontend Library",  badge:"Core" },
                { ico:"🟢", label:"Node.js",   sub:"Server Runtime",    badge:"Core" },
                { ico:"🍃", label:"MongoDB",   sub:"Database",          badge:"Certified" },
                { ico:"🐳", label:"Docker",    sub:"Containerization",  badge:"Active" },
              ].map((c,i) => (
                <div className="dj-fcard" key={i}>
                  <span className="dj-fcard-ico">{c.ico}</span>
                  <div>
                    <div className="dj-fcard-label">{c.label}</div>
                    <div className="dj-fcard-sub">{c.sub}</div>
                  </div>
                  <div className="dj-fcard-badge">{c.badge}</div>
                </div>
              ))}
            </div>
            <div className="dj-side-label">Full Stack · Real Time · Production</div>
          </div>
        </section>

        {/* TICKER */}
        <div className="dj-ticker">
          <div className="dj-ticker-track">
            {[...ticker,...ticker].map((t,i) => (
              <div className="dj-ticker-item" key={i}><div className="dj-ticker-dot"/>{t}</div>
            ))}
          </div>
        </div>

        {/* ABOUT */}
        <section className="dj-sec" id="about" ref={reg("about")}>
          <div className="dj-eyebrow" style={fadeL("about")}>01 — About</div>
          <h2 className="dj-sec-title" style={fade("about",0.1)}>Who I <em>Am</em></h2>
          <div className="dj-about-grid">
            <div className="dj-about-text" style={fade("about",0.15)}>
              <p>A <strong>passionate MERN Stack Developer</strong> who turns ideas into real, working products. From crafting smooth React interfaces to designing resilient Node.js APIs — every layer of the stack is a playground. Specialized in <strong>real-time applications</strong> that handle live data, complex interactions, and production-scale demands.</p>
              <p>Armed with <strong>MongoDB, AWS, and HTML certifications</strong>, the focus is always on shipping code that's clean, scalable, and built to last. Comfortable with Docker containers, GitHub pipelines, and AWS deployments — <strong>end-to-end ownership</strong> is the standard.</p>
            </div>
            <div className="dj-facts" style={fade("about",0.2)}>
              {[
                ["Stack","MERN Full Stack",true],
                ["Speciality","Real-Time Projects",false],
                ["Frontend","React.js",true],
                ["Backend","Node.js · Express.js",false],
                ["Database","MongoDB",true],
                ["DevOps","Docker · AWS",false],
              ].map(([k,v,r]) => (
                <div className="dj-fact" key={k}>
                  <span className="dj-fk">{k}</span>
                  <span className={`dj-fv${r?" cy":""}`}>{v}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CORE STACK */}
        <section className="dj-sec" id="stack" ref={reg("stack")}>
          <div className="dj-eyebrow" style={fadeL("stack")}>02 — Expertise</div>
          <h2 className="dj-sec-title" style={fade("stack",0.1)}>Core <em>Stack</em></h2>
          <div className="dj-stack-grid">
            {stacks.map((s,i) => (
              <div className="dj-sc" key={i} style={fade("stack", i*0.09)}>
                <div className="dj-sc-ghost">{s.n}</div>
                <div className="dj-sc-ico">{s.ico}</div>
                <div className="dj-sc-title">{s.title}</div>
                <div className="dj-sc-desc">{s.desc}</div>
              </div>
            ))}
          </div>
        </section>

        {/* PROJECTS */}
        <section className="dj-sec" id="projects" ref={reg("proj")}>
          <div className="dj-eyebrow" style={fadeL("proj")}>03 — Work</div>
          <h2 className="dj-sec-title" style={fade("proj",0.1)}>Real-Time <em>Projects</em></h2>
          <div className="dj-proj-list">
            {projects.map((p,i) => (
              <div className="dj-proj" key={i} style={fade("proj", i*0.1)}>
                <div className="dj-proj-n">0{i+1}</div>
                <div>
                  <div className="dj-proj-title">{p.title}</div>
                  <div className="dj-proj-desc">{p.desc}</div>
                  <div className="dj-proj-tags">
                    {p.tags.map(([t,c]) => <span key={t} className={`dj-ptag${c?" "+c:""}`}>{t}</span>)}
                  </div>
                </div>
                <div className="dj-proj-arr">→</div>
              </div>
            ))}
          </div>
        </section>

        {/* CERTIFICATIONS */}
        <section className="dj-sec" id="certifications" ref={reg("cert")}>
          <div className="dj-eyebrow" style={fadeL("cert")}>04 — Credentials</div>
          <h2 className="dj-sec-title" style={fade("cert",0.1)}><em>Certifi</em>cations</h2>
          <div className="dj-cert-grid">
            {[
              { ico:"🍃", name:"Associate Developer — MongoDB", org:"MongoDB Inc." },
              { ico:"☁️", name:"Cloud Practitioner — AWS",     org:"Amazon Web Services" },
              { ico:"🌐", name:"IT Specialist — HTML & CSS",   org:"Certiport" },
            ].map((c,i) => (
              <div className="dj-cert" key={i} style={fade("cert", i*0.12)}>
                <div className="dj-cert-orb">{c.ico}</div>
                <div className="dj-cert-name">{c.name}</div>
                <div className="dj-cert-org">{c.org}</div>
                <div className="dj-cert-badge">Verified</div>
              </div>
            ))}
          </div>
        </section>

        {/* TECH PILLS */}
        <section className="dj-sec" id="tech" ref={reg("tech")}>
          <div className="dj-eyebrow" style={fadeL("tech")}>05 — Skills</div>
          <h2 className="dj-sec-title" style={fade("tech",0.1)}>Technical <em>Stack</em></h2>
          <div className="dj-tech-grid">
            {[
              { label:"Frontend", pills:[["React.js","hot"],["JavaScript","hot"],["HTML5","hot"],["CSS3",""],["Tailwind",""],["Redux",""]] },
              { label:"Backend",  pills:[["Node.js","hot"],["Express.js","hot"],["REST APIs","hot"],["JWT Auth",""],["WebSockets",""],["Middleware",""]] },
              { label:"Database", pills:[["MongoDB","hot"],["Mongoose","hot"],["Aggregation",""],["Indexing",""],["Schema Design",""],["Firebase",""]] },
              { label:"DevOps & Cloud", pills:[["Docker","hot"],["AWS EC2",""],["AWS S3",""],["GitHub Actions",""],["CI/CD",""],["Nginx",""]] },
            ].map((g,i) => (
              <div className="dj-tg" key={i} style={fade("tech", i*0.09)}>
                <div className="dj-tg-title">{g.label}</div>
                <div className="dj-pills">
                  {g.pills.map(([p,c]) => <span key={p} className={`dj-pill${c?" "+c:""}`}>{p}</span>)}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* FOOTER */}
        <footer className="dj-footer">
          <div className="dj-footer-top">
            <div>
              <div className="dj-footer-role">MERN Stack<br /><span>Developer</span></div>
              <div className="dj-footer-sub">Building real-time, production-grade full-stack applications with React, Node.js, MongoDB, Docker &amp; AWS.</div>
            </div>
            <div className="dj-footer-certs">
              {[["🍃","MongoDB Associate Developer"],["☁️","AWS Cloud Practitioner"],["🌐","IT Specialist — HTML & CSS"]].map(([ico,label]) => (
                <div className="dj-footer-cert" key={label}>
                  <span className="dj-footer-cert-ico">{ico}</span>{label}
                </div>
              ))}
            </div>
          </div>
          <div className="dj-footer-bottom">
            <div className="dj-footer-copy">© 2024 · All rights reserved</div>
            <div className="dj-footer-loc">Aditya University · Kakinada, Andhra Pradesh</div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default DivyaPortfolio;